import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Plus, Calendar, Package, Play, Pause, Check, CheckCircle2, Truck, ShoppingCart } from "lucide-react";
import { showError } from "../lib/errorHandler";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  "plus": Plus, "calendar": Calendar, "box": Package, "play": Play, "pause": Pause,
  "check": Check, "check-circle": CheckCircle2, "package": Package, "truck": Truck,
  "shopping-cart": ShoppingCart,
};

const COLOR_CLASSES = {
  slate: { border: "border-slate-400", bg: "bg-slate-100", text: "text-slate-700", dot: "bg-slate-500" },
  blue: { border: "border-blue-400", bg: "bg-blue-50", text: "text-blue-700", dot: "bg-blue-500" },
  amber: { border: "border-amber-400", bg: "bg-amber-50", text: "text-amber-700", dot: "bg-amber-500" },
  emerald: { border: "border-emerald-400", bg: "bg-emerald-50", text: "text-emerald-700", dot: "bg-emerald-500" },
  rose: { border: "border-rose-400", bg: "bg-rose-50", text: "text-rose-700", dot: "bg-rose-500" },
  indigo: { border: "border-indigo-400", bg: "bg-indigo-50", text: "text-indigo-700", dot: "bg-indigo-500" },
  purple: { border: "border-purple-400", bg: "bg-purple-50", text: "text-purple-700", dot: "bg-purple-500" },
};

const fmt = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("pt-PT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

export default function OrderTimeline({ orderId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orderId) return;
    setLoading(true);
    axios.get(`${API}/orders/${orderId}/timeline`)
      .then(r => setData(r.data))
      .catch(e => showError(e, "Erro"))
      .finally(() => setLoading(false));
  }, [orderId]);

  if (loading) return <div className="p-6 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>;
  if (!data) return <div className="p-6 text-center text-sm text-muted-foreground">Sem dados</div>;
  if (!data.events || data.events.length === 0) return (
    <div className="p-6 text-center text-sm text-muted-foreground">Ainda não há eventos registados nesta ordem.</div>
  );

  return (
    <div className="p-4" data-testid="order-timeline">
      <div className="text-xs text-muted-foreground mb-3">
        <strong className="text-slate-700">{data.order_code}</strong> · Estado actual: <span className="uppercase font-bold">{data.current_status}</span> · {data.events.length} evento(s)
      </div>
      <div className="relative pl-6">
        {/* Vertical line */}
        <div className="absolute left-2.5 top-2 bottom-2 w-0.5 bg-slate-200" />
        {data.events.map((ev, i) => {
          const Icon = ICON_MAP[ev.icon] || Check;
          const colors = COLOR_CLASSES[ev.color] || COLOR_CLASSES.slate;
          return (
            <div key={i} className="relative mb-3 last:mb-0">
              {/* Dot */}
              <div className={`absolute -left-4 top-1 h-5 w-5 rounded-full ${colors.dot} flex items-center justify-center shadow ring-4 ring-white`}>
                <Icon className="h-3 w-3 text-white" />
              </div>
              <div className={`ml-2 rounded-lg border-l-4 ${colors.border} ${colors.bg} p-2.5`}>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className={`font-semibold text-sm ${colors.text}`}>{ev.label}</div>
                  <div className="text-[11px] text-slate-500 font-mono">{fmt(ev.timestamp)}</div>
                </div>
                {ev.description && (
                  <div className="text-xs text-slate-600 mt-0.5">{ev.description}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
